<template>
	<div class="referred-table-container">
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="active" @togglingPopup="v => $emit('toggling-popup', v)">
			<ModalHeader @click-button="applyBusinessCredit" :button-disabled="disabledContinue" class="bottomsheet-header" :title="getString('manage_rewards')"  :button-text="getString('continue')" />
			<div v-if="maxCredit && maxCredit > 0" class="number-container mb-20">
				<p>{{ getString('how_much_credit') }}</p>
				<AppNumberInput ref="credit-input" @changed-value="newSelectedCreditValue" style="min-width: 100px" :min="0" :max="maxCredit" double-decimal symbol="€" :value="0" />
			</div>
			<div v-else class="number-container mb-20">
				<p>{{ getString('no_credit_to_assign') }}</p>
			</div>
			<p class="second-title mb-10">{{ getString('assign_to_business') }}</p>
			<div
				class="business-assign-credit-button"
				v-for="(business, businessIndex) in availableBusinesses"
				:key="businessIndex"
				:class="{active: selectedBusinessId === business.business_id, pointer: maxCredit && maxCredit > 0}"
				@click="selectBusiness(business)"
			>
				<img :src="business.logo ? business.logo : 'https://qromomilan.s3.eu-south-1.amazonaws.com/img/vue/logo/rgb/sign-color.svg'" alt="test">
				<p>{{ business.name }}</p>
				<div class="business-credit-info">
					<p>{{ getString('available_credit') }}</p>
					<span :class="{'text-error': negativeBusinessCredit(business), 'text-primary': positiveBusinessCredit(business)}">{{ getPriceFormatted(business.open_balance, '€') }}</span>
				</div>
			</div>
		</AppBottomSheet>
		<AppNotification :title="getString('success')" :body-text="getString('success_assign_credit')" :type="1" ref="success-credit" />
		<AppNotification :title="getString('error')" :body-text="getString('error_assign_credit')" :type="-2" ref="fail-credit" />
	</div>
</template>

<script>
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppTable from "@/shared/components/AppTable.vue";
import AppButton from "@/shared/components/AppButton.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/user/stores";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import {getPriceFormatted} from "@/shared/helpers/strings";
import API from '@/shared/helpers/API.js';
import AppNotification from "@/shared/components/AppNotification.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, getPriceFormatted, currencyStore, userStore}
	},
	name: "RewardsManager",
	emits: ["toggling-popup"],
	components: {AppNotification, AppNumberInput, ModalHeader, AppButton, AppTable, AppBottomSheet},
	mixins: [WindowWidth],
	data() {
		return {
			loadingAssignCredit: false,
			selectedAssigningCredit: 0,
			selectedBusinessId: null
		}
	},
	props: {
		active: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		availableBusinesses() {
			let returningBusinesses = Object.assign({}, this.userStore.businesses);
			for(let business in returningBusinesses) {
				let businessCurrency = this.currencyStore.countryCurrency(returningBusinesses[business].country);
				if(businessCurrency.code !== 'EUR' || !returningBusinesses[business]?.permissions || !returningBusinesses[business].permissions.find(perm => perm === 'admin')) {
					delete returningBusinesses[business];
				}
			}
			return returningBusinesses;
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		maxCredit() {
			let finalCredit = this.userStore?.normalizedUser?.credit || 0;
			return finalCredit / 100;
		},
		disabledContinue() {
			return this.loadingAssignCredit || !this.selectedBusinessId || !this.selectedAssigningCredit || this.selectedAssigningCredit <= 0 || !this.maxCredit || this.selectedAssigningCredit <= 0;
		},
	},
	methods: {
		selectBusiness(business) {
			if(this.maxCredit && this.maxCredit > 0) {
				this.selectedBusinessId = business.business_id;
			}
		},
		newSelectedCreditValue(val) {
			this.selectedAssigningCredit = val || 0;
		},
		negativeBusinessCredit(business) {
			return business.open_balance && business.open_balance < 0;
		},
		positiveBusinessCredit(business) {
			return business.open_balance && business.open_balance > 0;
		},
		applyBusinessCredit() {
			if(this.disabledContinue || this.loadingAssignCredit) {
				return;
			}
			this.loadingAssignCredit = true;
			API.init().assignCreditToBusiness(this.selectedBusinessId, Math.floor(this.selectedAssigningCredit * 100), (resp) => {
				let notificationRef = resp && resp.result && resp.result === 'OK' ? 'success-credit' : 'fail-credit';
				this.userStore.refreshUserAccount(()=>{
					this.$refs['credit-input'].currentValue = 0;
					this.selectedBusinessId = null;
					this.loadingAssignCredit = false;
					this.$refs[notificationRef].openNotification();
				});
			});
		},
	},
}
</script>

<style scoped lang="scss">
.bottomsheet-header {
	background-color: var(--color-alpha-2);
}
.number-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: var(--color-bg-primary-2);
	padding: 20px 10px;
	border-radius: 13px;

	p {
		font-size: 16px;
	}
}
.second-title {
	@include noSelect;
	font-weight: 500;
	font-size: 18px;
}
.business-assign-credit-button {
	@include noSelect;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	background-color: rgba(7, 82, 163, 0.10);
	padding: 12px 20px;
	border-radius: 13px;
	margin-bottom: 5px;

	&.pointer {
		&.active {
			border: 1px solid var(--color-primary);
		}
		&:hover {
			background-color: rgba(7, 82, 163, 0.15);
		}
		&:active {
			background-color: rgba(7, 82, 163, 0.2);
		}
	}

	img {
		width: 30px;
		height: 30px;
		border-radius: 8px;
		margin-right: 10px;
	}
	p {
		font-size: 16px;
		font-weight: 500;
	}
	.business-credit-info {
		margin-left: auto;
		border-radius: 13px;
		padding: 5px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		box-shadow: inset 0 1px 2px var(--color-alpha-4);
		background-color: var(--bg-0);

		p {
			text-align: center;
			font-size: 10px;
			line-height: 12px;
			color: var(--color-txt-1);
		}
		span {
			font-size: 16px;

			&.text-primary {
				color: var(--color-primary);
			}
			&.text-error {
				color: var(--color-danger);
			}
		}
	}
	@media (max-width: 768px) {
		padding: 12px;
	}
}
</style>